<template>
  <div class="opened-orders">
    <Row
      v-for="(order, orderIndex) in orders"
      :key="orderIndex"
      class="opened-orders__row"
      chevron
      has-interaction
      :left-title="order.title"
      :has-line="orderIndex > 0"
      @click="openOrderDetails(order)"
    >
      <template #icon-left>
        <InitialAvatarIcon size="medium" :icon="order.icon" />
      </template>
      <template #top-right>
        <Money>{{ order.value.formatted }}</Money>
      </template>
      <template v-if="order.description" #bottom-left>
        <BaseText size="sm" :color="order.description.color">
          {{ order.description.label }}
        </BaseText>
      </template>
    </Row>
    <OrderDetailsSheet
      :is-open="isOrderDetailsOpen"
      :order-item="selectedOrder"
      @close="isOrderDetailsOpen = false"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router/composables';

import { Row, InitialAvatarIcon, BaseText } from '@warrenbrasil/nebraska-web';
import { Money } from '@/modules/common/components/Money';
import { OrderDetailsSheet } from '..';

import { IOrderItem } from '@/modules/wealth/services/portfolio-orders';

const route = useRoute();

interface Props {
  isLoading?: boolean;
  openedOrders: IOrderItem[];
  isScrolling?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  isScrolling: false
});

const isOrderDetailsOpen = ref(false);
const selectedOrder = ref<IOrderItem | null>(null);

const orders = computed(() => {
  return props.openedOrders;
});

const openOrderDetails = (order: IOrderItem) => {
  selectedOrder.value = order;
  isOrderDetailsOpen.value = true;
};

function orderSearcher() {
  if (!orders.value) {
    return () => null;
  }

  const allItemsWithOrders = orders.value.flatMap(order => {
    const products = order.details?.products || [];
    return products
      .flatMap(productGroup => productGroup.items || [])
      .map(item => ({ item, order }));
  });

  return function findOrderByProductIdOptimized(orderId: string) {
    const foundEntry = allItemsWithOrders.find(
      entry => entry.item.orderId === orderId
    );
    return foundEntry ? foundEntry.order : null;
  };
}

onMounted(() => {
  const orderId = route.query.orderId as string;

  if (orderId) {
    const searcher = orderSearcher();
    const order = searcher(orderId);

    if (order) {
      selectedOrder.value = order;
      isOrderDetailsOpen.value = true;
    }
  }
});
</script>

<style lang="less" scoped>
.opened-orders {
  padding-top: @size-spacing-x800;

  :deep(.row-item > div) {
    padding: @size-spacing-x450 0;
  }

  &__row {
    :deep(.container-right) {
      align-self: center;
    }

    :deep(.icon) {
      align-self: center;
    }
  }
}
</style>
